import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { AlertCircleIcon, Loader2Icon, LockKeyholeIcon } from "lucide-react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { PasswordInput } from "@/components/password-input";
import { ThemeToggle } from "@/components/theme-toggle";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/features/auth/auth-provider";
import { errorMessage } from "@/lib/http";

const searchSchema = z.object({
  /** Oturum sonlandığında kullanıcının bulunduğu sayfaya geri dönebilmek için. */
  redirect: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/giris")({
  validateSearch: searchSchema,
  beforeLoad: ({ context, search }) => {
    if (context.auth.status === "signedIn") {
      throw redirect({ to: search.redirect ?? "/" });
    }
  },
  component: LoginPage,
});

const formSchema = z.object({
  email: z.string().min(1, "E-posta zorunludur.").email("Geçerli bir e-posta adresi girin."),
  password: z.string().min(1, "Parola zorunludur."),
});

type FormValues = z.infer<typeof formSchema>;

function LoginPage() {
  const { signIn, status } = useAuth();
  const navigate = useNavigate();
  const search = Route.useSearch();

  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: "", password: "" },
  });

  async function onSubmit(values: FormValues) {
    setSubmitError(null);

    try {
      await signIn(values.email, values.password);
      await navigate({ to: search.redirect ?? "/" });
    } catch (error) {
      setSubmitError(errorMessage(error, "Giriş yapılamadı."));
      form.resetField("password");
    }
  }

  return (
    <div className="bg-muted/40 flex min-h-dvh flex-col">
      <div className="flex justify-end p-4">
        <ThemeToggle />
      </div>

      <div className="flex flex-1 items-center justify-center px-4 pb-20">
        <div className="w-full max-w-sm space-y-6">
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="bg-primary text-primary-foreground flex size-12 items-center justify-center rounded-xl">
              <LockKeyholeIcon className="size-6" />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight">EBridge Lisans</h1>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Giriş yap</CardTitle>
              <CardDescription className="text-wrap">
                Hesabınız bulunmuyorsa kayıt için firma yetkiliniz ile görüşün.
              </CardDescription>
            </CardHeader>

            <CardContent>
              {status === "inactive" && (
                <Alert variant="warning" className="mb-4">
                  <AlertCircleIcon />
                  <AlertTitle>Hesap pasif</AlertTitle>
                  <AlertDescription>
                    Bu hesap pasife alınmış; giriş yapamazsınız. Yöneticinize başvurun.
                  </AlertDescription>
                </Alert>
              )}

              {submitError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircleIcon />
                  <AlertTitle>Giriş yapılamadı</AlertTitle>
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              )}

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>E-posta</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            autoComplete="username"
                            autoFocus
                            placeholder="ad.soyad@saykom.com.tr"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Parola</FormLabel>
                        <FormControl>
                          <PasswordInput autoComplete="current-password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
                    {form.formState.isSubmitting && <Loader2Icon className="animate-spin" />}
                    Giriş yap
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>

          <p className="text-center text-sm">
            <Link to="/indirmeler" className="text-primary underline-offset-4 hover:underline">
              Kurulum dosyalarını indirin
            </Link>
          </p>

          <p className="text-muted-foreground text-center text-xs text-balance">
            Saykom Bilgisayar
          </p>
        </div>
      </div>
    </div>
  );
}
